import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, Trash2, ShieldCheck, Shield, X } from 'lucide-react';
import { adminApi } from '../../api/admin';
import api from '../../api/client';

/**
 * TeamMembersPanel — roster + add/remove/promote/demote for one team.
 *
 * Used inside the expanded row of TeamsManager. Shows the team's current
 * members and lets eligible users (tenant admin, team admin of THIS team)
 * mutate the roster.
 *
 * Backend rules enforced server-side:
 *  - Self-removal blocked (hide the button with tooltip)
 *  - Last team admin demote/remove blocked unless caller is tenant admin
 */
export default function TeamMembersPanel({ team, currentUser, onChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [makeAdmin, setMakeAdmin] = useState(false);

  const isTenantAdmin = currentUser?.is_admin === true;

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.listTeamMembers(team.id);
      setMembers(res.data?.members || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const openAddModal = useCallback(async () => {
    try {
      // Load tenant users that aren't already on the team
      const res = await api.get('/admin/users');
      const allUsers = res.data?.users || [];
      const memberIds = new Set(members.map((m) => m.user_id));
      setTenantUsers(allUsers.filter((u) => !memberIds.has(u.id) && u.is_active));
      setSelectedUserId('');
      setMakeAdmin(false);
      setShowAdd(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load users');
    }
  }, [members]);

  const handleAdd = async () => {
    if (!selectedUserId) {
      toast.error('Pick a user');
      return;
    }
    try {
      await adminApi.addTeamMember(team.id, { user_id: selectedUserId, is_team_admin: makeAdmin });
      toast.success('Member added');
      setShowAdd(false);
      loadMembers();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add member');
    }
  };

  const handleTogglePromote = async (member) => {
    try {
      await adminApi.updateTeamMember(team.id, member.user_id, { is_team_admin: !member.is_team_admin });
      toast.success(member.is_team_admin ? 'Demoted from team admin' : 'Promoted to team admin');
      loadMembers();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update member');
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.first_name} ${member.last_name} from this team?`)) return;
    try {
      await adminApi.removeTeamMember(team.id, member.user_id);
      toast.success('Member removed');
      loadMembers();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="animate-pulse h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Members ({members.length})</h3>
        <button
          onClick={() => openAddModal()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-xs font-medium rounded-md hover:bg-brand-700"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add Member
        </button>
      </div>

      {members.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-4 text-center">No members yet.</div>
      ) : (
        <div className="bg-white rounded border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-center">Team Admin</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((m) => {
                const isSelf = m.user_id === currentUser?.id;
                return (
                  <tr key={m.user_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {m.first_name} {m.last_name}
                      {isSelf && <span className="ml-1.5 text-xs text-gray-400">(you)</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{m.email}</td>
                    <td className="px-3 py-2 text-gray-600">{m.role_name || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {m.is_team_admin ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded">
                          <ShieldCheck className="h-3 w-3" />
                          Team Admin
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <button
                        onClick={() => handleTogglePromote(m)}
                        className="inline-flex items-center px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                        title={m.is_team_admin ? 'Demote from team admin' : 'Promote to team admin'}
                      >
                        {m.is_team_admin ? <Shield className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      </button>
                      {isSelf ? (
                        <button
                          disabled
                          className="inline-flex items-center px-2 py-1 text-xs text-gray-300 cursor-not-allowed"
                          title="You cannot remove yourself from a team"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRemove(m)}
                          className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                          title="Remove from team"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add Member to {team.name}</h2>
              <button
                onClick={() => setShowAdd(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— Select user —</option>
                  {tenantUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </option>
                  ))}
                </select>
                {tenantUsers.length === 0 && (
                  <div className="text-xs text-gray-500 italic mt-1">All active users are already on this team.</div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={makeAdmin}
                  onChange={(e) => setMakeAdmin(e.target.checked)}
                />
                Make team admin
              </label>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAdd()}
                disabled={!selectedUserId}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
